import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import MigrationCrunchFooter from '../components/MigrationCrunchFooter';
import {
  Database, ArrowRight, Sparkle, CheckCircle, WarningCircle,
  CurrencyDollar, Calculator, Scales,
} from '@phosphor-icons/react';

/**
 * /migrate/salesforce-true-cost — CFO-math landing page.
 *
 * SEO target queries:
 *   "true cost of salesforce"
 *   "salesforce total cost of ownership small business"
 *   "how much does salesforce really cost"
 *   "salesforce hidden costs"
 *
 * Every number is conservative and defensible: license prices are
 * Salesforce's published list pricing (2026); setup and admin figures
 * use the LOW end of published SMB implementation ranges. The "what we
 * left out" section makes the conservatism explicit — all omissions
 * favor Salesforce.
 */

const EDITIONS = [
  { id: 'starter', name: 'Starter Suite', perSeat: 25, setup: 1500, adminMo: 100 },
  { id: 'pro', name: 'Pro Suite', perSeat: 100, setup: 5000, adminMo: 300 },
  { id: 'enterprise', name: 'Enterprise', perSeat: 165, setup: 7500, adminMo: 500 },
  { id: 'unlimited', name: 'Unlimited', perSeat: 330, setup: 10000, adminMo: 750 },
];

const MONTHS = 36;
const SC_MONTHLY_PER_USER = 30;

function fmt(n) {
  return `$${Math.round(n).toLocaleString('en-US')}`;
}

export default function SalesforceTrueCostPage() {
  const [seats, setSeats] = useState(5);
  const [editionId, setEditionId] = useState('pro');

  const math = useMemo(() => {
    const ed = EDITIONS.find((e) => e.id === editionId);
    const licenses = seats * ed.perSeat * MONTHS;
    const setup = ed.setup;
    const admin = ed.adminMo * MONTHS;
    const sfTotal = licenses + setup + admin;
    const scSub = SC_MONTHLY_PER_USER * seats * MONTHS;
    const scTotal = scSub; // migration to SalesCrunchCRM is free
    const savings = Math.max(0, sfTotal - scTotal);
    const pct = sfTotal > 0 ? Math.round((savings / sfTotal) * 100) : 0;
    return { ed, licenses, setup, admin, sfTotal, scSub, scTotal, savings, pct };
  }, [seats, editionId]);

  // SEO: title + meta description + OG/Twitter cards, restored on unmount.
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'The True Cost of Salesforce (2026 Math) | MigrationCrunch';
    const desc =
      'Licenses + setup + admin: what Salesforce really costs a small team over 3 years — and the $30/user/mo alternative. Conservative, line-item CFO math.';
    const upsertMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      let created = false;
      let prev = null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created = true;
      } else {
        prev = el.getAttribute('content');
      }
      el.setAttribute('content', content);
      return () => {
        if (created) el.remove();
        else if (prev !== null) el.setAttribute('content', prev);
      };
    };
    const url = `${window.location.origin}/migrate/salesforce-true-cost`;
    const img = `${window.location.origin}/og-default.svg`;
    const restorers = [
      upsertMeta('name', 'description', desc),
      upsertMeta('property', 'og:title', document.title),
      upsertMeta('property', 'og:description', desc),
      upsertMeta('property', 'og:url', url),
      upsertMeta('property', 'og:type', 'website'),
      upsertMeta('property', 'og:site_name', 'MigrationCrunch'),
      upsertMeta('property', 'og:image', img),
      upsertMeta('name', 'twitter:card', 'summary_large_image'),
      upsertMeta('name', 'twitter:title', document.title),
      upsertMeta('name', 'twitter:description', desc),
      upsertMeta('name', 'twitter:image', img),
    ];
    return () => {
      document.title = prevTitle;
      restorers.forEach((fn) => fn());
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0F0F1A] text-white" data-testid="sf-cost-page">
      {/* Shared MigrationCrunch nav */}
      <header className="border-b border-white/10 px-6 py-4 sticky top-0 bg-[#0F0F1A]/95 backdrop-blur-sm z-30">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <Link to="/migrate" className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="mc-nav-logo">
            <Database size={28} weight="fill" className="text-[#F39C12]" />
            <span className="text-xl font-black">Migration<span className="text-[#F39C12]">Crunch</span></span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <Link to="/migrate#fit-check" className="text-white/70 hover:text-amber-300 transition-colors" data-testid="mc-nav-fit">
              Fit check
            </Link>
            <Link to="/migrate/compare" className="text-white/70 hover:text-amber-300 transition-colors" data-testid="mc-nav-compare">
              Compare vendors
            </Link>
            <Link to="/migrate#pricing" className="text-white/70 hover:text-amber-300 transition-colors" data-testid="mc-nav-pricing">
              Pricing
            </Link>
            <a href="/" className="text-sm text-white/70 hover:text-white border border-white/20 hover:border-white/40 rounded px-3 py-1.5 transition-colors" data-testid="mc-nav-try-crm">
              Try SalesCrunchCRM →
            </a>
          </nav>
          <a href="/" className="md:hidden text-xs text-white/70 hover:text-white border border-white/20 rounded px-2 py-1 transition-colors" data-testid="mc-nav-try-crm-mobile">
            CRM →
          </a>
        </div>
      </header>

      {/* HERO */}
      <section className="px-6 py-16 md:py-20 border-b border-white/10">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest text-amber-300 uppercase mb-3">
            <Calculator size={12} weight="fill" /> Line-item CFO math · 2026 list pricing
          </div>
          <h1 className="text-4xl md:text-6xl font-black mb-5 leading-tight" data-testid="sf-cost-headline">
            The true cost of Salesforce.{' '}
            <span className="text-amber-400">All of it.</span>
          </h1>
          <p className="text-lg md:text-xl text-white/75 max-w-3xl mx-auto leading-relaxed">
            The per-seat price is the part they advertise. The setup invoice, the admin
            hours, and the renewal hikes are the part your CFO finds out about later.
            Here&apos;s the whole bill, line by line — using Salesforce&apos;s own published
            pricing and the <em className="text-white">low end</em> of real-world setup costs.
          </p>
        </div>
      </section>

      {/* CALCULATOR */}
      <section className="px-6 py-16 border-b border-white/10 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="text-white/60 text-sm">Team size</span>
              <input
                type="range" min={1} max={50} value={seats}
                onChange={(e) => setSeats(Number(e.target.value))}
                className="w-40 accent-amber-400"
                data-testid="sf-cost-seats-slider"
              />
              <span className="text-amber-300 font-black text-lg w-20" data-testid="sf-cost-seats-value">{seats} {seats === 1 ? 'seat' : 'seats'}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-white/60 text-sm">Salesforce edition</span>
              <select
                value={editionId}
                onChange={(e) => setEditionId(e.target.value)}
                className="bg-white/10 hover:bg-white/15 border border-amber-400/40 hover:border-amber-400 text-white text-sm font-bold px-3 py-1.5 rounded outline-none cursor-pointer transition-colors"
                data-testid="sf-cost-edition-select"
              >
                {EDITIONS.map((e) => (
                  <option key={e.id} value={e.id} className="bg-[#0F0F1A]">
                    {e.name} — ${e.perSeat}/user/mo
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Salesforce column */}
            <div className="bg-rose-500/[0.04] border border-rose-500/20 rounded-lg overflow-hidden" data-testid="sf-cost-sf-column">
              <div className="px-5 py-4 border-b border-rose-500/20 bg-black/20">
                <div className="text-[10px] uppercase tracking-widest text-rose-300 font-black">3-year cost · staying on Salesforce</div>
                <div className="text-lg font-black">{math.ed.name} · {seats} {seats === 1 ? 'seat' : 'seats'}</div>
              </div>
              <div className="px-5 py-4 space-y-3 text-sm">
                <CostLine
                  label={`Licenses — ${seats} × $${math.ed.perSeat}/user/mo × 36 mo`}
                  value={fmt(math.licenses)}
                  testid="sf-cost-line-licenses"
                />
                <CostLine
                  label="Implementation / setup (low-end SMB quote)"
                  value={fmt(math.setup)}
                  testid="sf-cost-line-setup"
                />
                <CostLine
                  label={`Admin & consultant upkeep — $${math.ed.adminMo}/mo × 36`}
                  value={fmt(math.admin)}
                  testid="sf-cost-line-admin"
                />
              </div>
              <div className="px-5 py-4 border-t border-rose-500/20 bg-black/30 flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-rose-300 font-black">3-year total</span>
                <span className="text-2xl font-black text-rose-300" data-testid="sf-cost-sf-total">{fmt(math.sfTotal)}</span>
              </div>
            </div>

            {/* SalesCrunch column */}
            <div className="bg-emerald-500/[0.04] border border-emerald-500/30 rounded-lg overflow-hidden" data-testid="sf-cost-sc-column">
              <div className="px-5 py-4 border-b border-emerald-500/20 bg-black/20">
                <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">3-year cost · switching to SalesCrunchCRM</div>
                <div className="text-lg font-black">Same team · $30/user/mo</div>
              </div>
              <div className="px-5 py-4 space-y-3 text-sm">
                <CostLine
                  label="Migration (contacts + notes + history, AI note recovery)"
                  value="$0 — free"
                  tone="emerald"
                  testid="sf-cost-line-migration"
                />
                <CostLine
                  label={`Subscription — ${seats} × $30/user/mo × 36 mo`}
                  value={fmt(math.scSub)}
                  tone="emerald"
                  testid="sf-cost-line-sub"
                />
                <CostLine label="Setup, admin, consultants, training" value="$0" tone="emerald" testid="sf-cost-line-zero" />
              </div>
              <div className="px-5 py-4 border-t border-emerald-500/20 bg-black/30 flex items-center justify-between">
                <span className="text-xs uppercase tracking-widest text-emerald-300 font-black">3-year total</span>
                <span className="text-2xl font-black text-emerald-300" data-testid="sf-cost-sc-total">{fmt(math.scTotal)}</span>
              </div>
            </div>
          </div>

          {/* Savings banner */}
          <div className="mt-6 bg-amber-400/10 border border-amber-400/40 rounded-lg px-6 py-5 text-center" data-testid="sf-cost-savings">
            <div className="text-xs uppercase tracking-widest text-amber-300 font-black mb-1">Your CFO keeps</div>
            <div className="text-4xl md:text-5xl font-black text-amber-300">{fmt(math.savings)}</div>
            <div className="text-white/60 text-sm mt-1">
              over 3 years — a <strong className="text-white">{math.pct}%</strong> reduction, before counting a single Salesforce add-on.
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE LEFT OUT */}
      <section className="px-6 py-16 border-b border-white/10">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-black text-center mb-3">
            What we left <span className="text-rose-300">out</span> of the Salesforce column.
          </h2>
          <p className="text-center text-white/60 mb-8 text-sm md:text-base">
            Every omission favors Salesforce. Add these back in and the gap gets wider:
          </p>
          <ul className="space-y-3" data-testid="sf-cost-omissions">
            {[
              ['Einstein / Agentforce AI add-ons', 'roughly $50+/user/mo extra for the AI features the demos show you.'],
              ['Data storage overages', 'base orgs get ~10 GB; extra blocks are billed monthly and add up fast on note-heavy databases.'],
              ['Premier Success support plan', 'typically ~30% of your net license spend, per year, if you want fast human help.'],
              ['Training and admin certification', 'courses, certs, and the ramp-up weeks for whoever gets "voluntold" to be the admin.'],
              ['Renewal price increases', 'Salesforce raised list prices ~9% in recent cycles; multi-year contracts bake in escalators.'],
            ].map(([title, body]) => (
              <li key={title} className="flex items-start gap-3 bg-white/[0.03] border border-white/10 rounded px-4 py-3">
                <WarningCircle size={18} weight="fill" className="text-rose-300 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <strong className="text-white">{title}</strong>
                  <span className="text-white/65"> — {body}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* HONESTY: WHEN SALESFORCE IS WORTH IT */}
      <section className="px-6 py-16 border-b border-white/10 bg-white/[0.02]">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-1 text-[10px] font-bold tracking-widest text-emerald-300 uppercase mb-3">
            <Scales size={12} weight="fill" /> Honesty over hype
          </div>
          <h2 className="text-2xl md:text-3xl font-black mb-4">
            And when Salesforce <span className="text-emerald-400">is</span> worth every penny.
          </h2>
          <p className="text-white/70 text-sm md:text-base leading-relaxed mb-6" data-testid="sf-cost-honesty">
            If your business runs on custom objects, multi-step approval workflows, and
            deep integrations maintained by a dedicated admin team — Salesforce is genuinely
            the right tool, and this page&apos;s math doesn&apos;t apply to you. This page is for
            the other 80%: small sales teams paying enterprise prices for a contact list,
            a pipeline, and notes.
          </p>
          <a
            href="/migrate#fit-check"
            className="inline-flex items-center gap-2 text-sm font-bold text-emerald-300 hover:text-emerald-200 underline underline-offset-4 decoration-emerald-400/40 transition-colors"
            data-testid="sf-cost-fit-link"
          >
            Not sure which camp you&apos;re in? Take the 30-second fit check <ArrowRight size={14} weight="bold" />
          </a>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-20">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-400/30 px-3 py-1 rounded-full text-[12px] text-emerald-300 mb-5">
            <CheckCircle size={12} weight="fill" /> Migration to SalesCrunchCRM is free — contacts, notes, and history included
          </div>
          <h2 className="text-3xl md:text-4xl font-black mb-4">
            Keep the pipeline. Lose the invoice.
          </h2>
          <p className="text-white/70 mb-8 text-base md:text-lg">
            Our patent-pending AI moves your Salesforce contacts, deals, and the
            relationships buried inside years of typed notes — in minutes, for free,
            into a CRM that costs $30 per user per month — no tiers, no add-ons.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/migrate/salesforce-to-act"
              className="px-6 py-3 bg-amber-400 hover:bg-amber-300 text-black font-bold rounded-md transition-colors inline-flex items-center justify-center gap-2"
              data-testid="sf-cost-migrate-cta"
            >
              <Sparkle size={16} weight="fill" />
              See the Salesforce escape guide
            </Link>
            <Link
              to="/migrate"
              className="px-6 py-3 border border-white/20 hover:border-white/40 text-white font-medium rounded-md transition-colors inline-flex items-center justify-center gap-2"
              data-testid="sf-cost-home-cta"
            >
              <CurrencyDollar size={16} weight="bold" />
              Migrating somewhere else? Start here
            </Link>
          </div>
          <p className="text-xs text-white/40 mt-6">
            Salesforce list pricing as of 2026. Setup and admin figures use the low end of
            published SMB implementation ranges. Salesforce is a trademark of Salesforce, Inc.
          </p>
        </div>
      </section>

      <MigrationCrunchFooter />
    </div>
  );
}

function CostLine({ label, value, tone = 'rose', testid }) {
  return (
    <div className="flex items-start justify-between gap-4" data-testid={testid}>
      <span className="text-white/70">{label}</span>
      <span className={`font-bold whitespace-nowrap ${tone === 'emerald' ? 'text-emerald-300' : 'text-white/90'}`}>{value}</span>
    </div>
  );
}
